import { Component, Input, isDevMode, OnInit } from '@angular/core';
import { DealerProfile } from '@app/domain/model/dealer-profile';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportsSearchParamsModel } from './reports-search-params.model';
import { ReplaySubject, Subscription } from 'rxjs';
import { AdminSearchResultsModel } from '@app/feature/admin/search/admin-search-results.model';
import { AppError } from '@app/domain/model/error';
import { PurchaseReport } from '@app/feature/dealer/report/purchase-report';
import { ListReportsService } from '@app/feature/dealer/report-search/list-reports.service';
import { DatepickerViewModel } from '@ng-bootstrap/ng-bootstrap/datepicker/datepicker-view-model';
import { NgbDate, NgbDateParserFormatter, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApprovalStatus } from '@app/domain/model/approval-status';
import { ReportType } from '@app/domain/model/report-type';
import { ReportingPeriod } from '@app/feature/dealer/report/reporting-period';
import { PurchaseReceiptReportComponent } from '@app/feature/dealer/report/purchase-receipt-report/purchase-receipt-report.component';
import { ReadReportingPeriodService } from '@app/feature/dealer/report/read-reporting-period.service';
import { UserAuthority } from '@app/domain/model/user-authority';
import { DidNotPurchaseService } from '@app/feature/dealer/report/did-not-purchase-modal/did-not-purchase.service';
import { AuthenticationService } from '@app/shared/services/authentication.service';
import { PurchaseReceiptReportService } from '@app/feature/dealer/report/purchase-receipt-report/purchase-receipt-report.service';
import { SetStatusService } from '@app/feature/dealer/report/set-status/set-status.service';
import { DatePickerFormatter } from '@app/util/date-picker-formatter';
import { PurchaseReportCreationEditingComponent } from '@app/feature/dealer/report/purchase-report-creation-editing/purchase-report-creation-editing.component';
import { PaperReportComponent } from '@app/feature/admin/quick-add/paper-report/paper-report.component';
import { PaperCreditComponent } from '@app/feature/admin/quick-add/paper-credit/paper-credit.component';
import { takeUntil } from 'rxjs/operators';
import { Months } from '@app/domain/model/months';

@Component({
  providers: [{ provide: NgbDateParserFormatter, useClass: DatePickerFormatter }],
  selector: 'app-dealer-report-search-view',
  templateUrl: './dealer-report-search-view.component.html',
  styleUrls: ['./dealer-report-search-view.component.scss']
})
export class DealerReportSearchViewComponent implements OnInit {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  // make the enums' values accessible from template
  public ApprovalStatus: typeof ApprovalStatus = ApprovalStatus;
  public ReportType: typeof ReportType = ReportType;

  @Input()
  profile: DealerProfile;

  searchForm: FormGroup;

  private searchResults: PurchaseReport[];

  private searchParams: ReportsSearchParamsModel;

  private queryParamsSubscription: Subscription;

  private activeColumn: number;

  isSearchPerformed = false;

  private isTablePaginationHidden: boolean;

  readonly numberOfDisplayedRows = 10;


  constructor(private readonly router: Router,
              private readonly activatedRoute: ActivatedRoute,
              private readonly formBuilder: FormBuilder,
              private readonly listReportService: ListReportsService,
              private readonly setStatusService: SetStatusService,
              private readonly modalService: NgbModal,
              private readonly authenticator: AuthenticationService) { }

  ngOnInit() {
    this.initForm();
    this.getPublicSearchQueryParams();

  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private initForm() {
    this.searchForm = this.formBuilder.group({
      purchaseDate: ['', Validators.required],
      fishermanLicense: ['', Validators.required],
    });
  }

  onSubmit() {
    if (this.searchForm.valid) {
      localStorage.setItem('searchReportParams', JSON.stringify(this.searchForm.value));
      this.router.navigate([], {
        queryParams: this.addParamsToUrl(this.searchForm.value)
      });
    }
  }

  private onStatusChange(reportId: number, status: ApprovalStatus) {
    this.setStatusService.setStatus(reportId, status).subscribe((data) => {
      if (isDevMode()) {
        console.log(data);
      }
    });
  }

  onStartNewSearch() {
    this.activeColumn = -1;
    this.searchForm.reset();
    this.isSearchPerformed = false;
    this.deleteQueryParamsFromUrl();
  }
  /**
   * Open the 'Add Purchase Report' modal dialog.
   *
   * @param period (optional) period for which we are adding the report
   */
  onAddPurchase() {
    const modal = this.modalService.open(
      PurchaseReportCreationEditingComponent,
      { backdrop: 'static', keyboard: false, size: 'lg', windowClass: 'purchase-report' }
    );

    modal.componentInstance.profile = this.profile;
    modal.componentInstance.marketTypes = this.profile.marketTypes;
    modal.result.then((isCreated: boolean) => {
      if (!isCreated) {
        return;
      }
      this.performSearch(this.searchParams);
    });
  }

  onOpenPurchaseReceiptReport(reportId: number, submittedBy: string, periodIndex: number) {

    const modal = this.modalService.open(
      PurchaseReceiptReportComponent,
      { backdrop: 'static', keyboard: false, size: 'lg', windowClass: 'purchase-report' }
    );

    modal.componentInstance.reportId = reportId;
    modal.componentInstance.submittedBy = submittedBy;
    modal.componentInstance.marketTypes = this.profile.marketTypes;
    modal.componentInstance.profile = this.profile;

    modal.result.then((isUpdated: boolean) => {
      this.performSearch(this.searchParams);
    });
  }
  private performSearch(searchParams: ReportsSearchParamsModel) {
    this.listReportService.readReportsList(searchParams.purchaseDate, searchParams.fishermanLicense, this.getDealerId()).subscribe((result: PurchaseReport[]) => {
        if (result.length > 0) {
          if (result.length < this.numberOfDisplayedRows) {
            this.isTablePaginationHidden = true;
          } else {
            this.isTablePaginationHidden = false;
          }
          this.searchResults = result;
          this.isSearchPerformed = true;
        } else {
          this.isSearchPerformed = false;
          this.searchForm.setErrors({ 'noResultsFound': true });
        }
      },
      (error: AppError) => {
        this.deleteQueryParamsFromUrl();
        this.isSearchPerformed = false;
        if (error.error && Array.isArray(error.error) && error.error[0].code === 'tooManyResults') {
          this.searchForm.setErrors({ 'tooManyResults': true });
        } else {
          throw error;
        }
      });
  }

  private addParamsToUrl(searchParams: any) {
    const params = {};

    ['purchaseDate', 'fishermanLicense'].map((param) => {
      if (!searchParams[param]) {
        return;
      }
      if(param === 'purchaseDate' ){
        const purchaseDate: NgbDate = searchParams[param];
        params[param] = purchaseDate.year.toString() + (purchaseDate.month >=10 ? purchaseDate.month : '0' + purchaseDate.month)  + (purchaseDate.day >= 10 ? purchaseDate.day : '0' + purchaseDate.day);
      } else {
        params[param] = searchParams[param].toString().trim();
      }
    });

    return params;
  }

  private getPublicSearchQueryParams() {
    this.queryParamsSubscription = this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe((params: ReportsSearchParamsModel) => {
        this.searchParams = params;
        if (!Object.keys(params).length) {
          return;
        }
        this.searchForm.patchValue(JSON.parse(localStorage.getItem('searchReportParams')));
        this.performSearch(params);
      });
  }

  private deleteQueryParamsFromUrl() {
    this.router.navigate([], {
      queryParams: {}
    });
  }

  private getDealerId() {
    const executor = this.authenticator.getCurrentUser();
    return executor.hasAuthority(UserAuthority.DEALER_INFO) ? this.profile.id : null;
  }

  hasManageReportAuthority() {
    const executor = this.authenticator.getCurrentUser();
    return executor.hasAuthority(UserAuthority.MANAGE_REPORT);
  }

  /**
   * Open the 'Add Paper Report' modal dialog.
   *
   * @param period (optional) period for which we are adding the report
   */
  addPaperReport() {
    const modal = this.modalService.open(
      PaperReportComponent,
      { backdrop: 'static', centered: true, keyboard: false, size: 'lg', windowClass: 'paper-report' }
    );
    modal.componentInstance.dealer = this.profile;

    modal.result.then((isCreated: boolean) => {
      if (!isCreated) {
        return;
      }
    });
  }

  /**
   * Open the 'Add Paper Credit' modal dialog.
   *
   * @param period (optional) period for which we are adding the report
   */
  addPaperCredit() {
    const modal = this.modalService.open(
      PaperCreditComponent,
      { backdrop: 'static', centered: true, keyboard: false, size: 'lg', windowClass: 'paper-credit' }
    );
    modal.componentInstance.dealer = this.profile;

    modal.result.then((isCreated: boolean) => {
      if (!isCreated) {
        return;
      }
    });
  }

}
